import {
  CUSTOMERS_CSV_MAX_ROWS,
  customersCsvFilename,
  DEFAULT_CUSTOMER_BASIS,
  DEFAULT_CUSTOMER_SORT,
  DEFAULT_SORT_ORDER,
  type ConsentState,
  type CustomerBasis,
  type CustomerReveal,
  type CustomerRow,
  type CustomerSearch,
  type CustomersList,
  type CustomersQuery,
} from '@/api/customers'
import type { CsvFile } from '@/api/list'
import type { PeriodInfo } from '@/api/types'
import { minorUnitExponent } from '@/design/format'
import { ApiError } from '@/lib/api'
import { addDays, dayCount, resolvePeriod, type DateWindow } from '@/lib/filters'
import { DEMO_TIMEZONE, scopeFor, type DemoDataset } from '@/mock/generators/engine'
import { unitNoise } from '@/mock/random'

import { csvBlob, csvInstant, csvMoney, csvPlain, csvText, type MockCsvColumn } from '../list/mockCsv'
import { sortCustomers } from './sorting'

/**
 * The demo tenant's customers, by the API's rules
 * (`customer_list/README.md`): who is listed by `basis`, new and
 * returning in the range, nulls last in either order with ties by website and id, the
 * README's masking, unsynced customers with no record (and so no `customer_id`), and
 * the CSV's columns. Each demo customer is a pure function of the tenant's seed and its
 * index, so a customer is the same whichever page or period asks. The demo has no
 * email addresses: an email search finds `customer<id>@example.com` when that customer
 * granted advertising consent, and nothing else.
 */

const POOL_SIZE = 360
const DEFAULT_LIMIT = 50
/** How far back the demo's registrations and orders reach. */
const HISTORY_DAYS = 420

/** Every column `not_available` names on an unsynced row (README *Unsynced customers*). */
const RECORD_FIELDS = ['registeredAt', 'isMember', 'isVip', 'consent', 'firstTouch'] as const

const TOUCHES = [
  { source: 'facebook', medium: 'paid_social', campaign: 'autumn-cars' },
  { source: 'google', medium: 'organic', campaign: null },
  { source: 'newsletter', medium: 'email', campaign: 'weekly-draws' },
  { source: 'tiktok', medium: 'paid_social', campaign: 'cash-friday' },
] as const

interface DemoOrder {
  /** The tenant day it completed. */
  day: string
  at: string
  tickets: number
  revenueMinor: number
  refundMinor: number
}

/** What the website's own customer record holds, once it has synced. */
type DemoRecord = Pick<CustomerRow, 'firstTouch' | 'isMember' | 'isVip' | 'consent'>

/** One demo customer, each fact held once: the row is built from it per period (`rowFor`). */
interface DemoCustomer {
  /** The website's own id: masked on every row, shown only by the reveal. */
  externalId: string
  websiteId: string
  /** The tenant day they registered; null while their record has not synced. */
  registeredDay: string | null
  /** Their synced record; null while it has not synced. */
  record: DemoRecord | null
  orders: DemoOrder[]
}

/** The Suite's id of a synced customer's record; an unsynced customer has none. */
function customerIdOf(customer: DemoCustomer): string | null {
  return customer.record ? `cus_demo_${customer.externalId}` : null
}

/** README *Masking*: four fixed dots, then at most a third of the id (and at most four characters). */
export function maskCustomerRef(externalId: string): string {
  const shown = Math.min(4, Math.floor(externalId.length / 3))
  return `••••${shown > 0 ? externalId.slice(-shown) : ''}`
}

function pick<T>(items: readonly T[], noise: number): T {
  return items[Math.min(items.length - 1, Math.floor(noise * items.length))] as T
}

function consentState(noise: number): ConsentState {
  return noise < 0.55 ? 'granted' : noise < 0.75 ? 'denied' : noise < 0.8 ? 'withdrawn' : 'unknown'
}

function demoCustomer(seed: number, today: string, website: string, index: number, unit: number): DemoCustomer {
  const noise = (...key: Array<string | number>) => unitNoise(seed, 'customer', index, ...key)
  const externalId = String(1180 + index * 3)
  // One customer in twelve has bought but its record has not synced yet.
  const synced = noise('synced') >= 1 / 12
  const registeredDay = synced ? addDays(today, -Math.floor(noise('registered') * HISTORY_DAYS)) : null
  const firstDay = registeredDay ?? addDays(today, -Math.floor(noise('first') * 60))
  const span = dayCount(firstDay, today)
  // A third of synced customers never buy; an unsynced one is only known from an order.
  const orderCount = synced && noise('buys') < 0.33 ? 0 : 1 + Math.floor(noise('count') ** 2 * 14)
  const orders: DemoOrder[] = Array.from({ length: orderCount }, (_, order) => {
    const day = addDays(firstDay, Math.floor(noise('order', order) * span))
    const tickets = 1 + Math.floor(noise('tickets', order) * 20)
    const revenueMinor = Math.round(tickets * 1.99 * unit)
    const refundMinor = noise('refund', order) < 0.04 ? Math.round(revenueMinor / 2) : 0
    const hour = String(8 + Math.floor(noise('hour', order) * 14)).padStart(2, '0')
    return { day, at: `${day}T${hour}:12:00Z`, tickets, revenueMinor, refundMinor }
  }).sort((a, b) => a.at.localeCompare(b.at))
  const touch = synced && noise('touch') < 0.7 ? pick(TOUCHES, noise('touch-kind')) : null
  return {
    externalId,
    websiteId: website,
    registeredDay,
    orders,
    record:
      synced && registeredDay
        ? {
            firstTouch: touch
              ? { touchedAt: `${registeredDay}T09:40:00Z`, source: touch.source, medium: touch.medium, campaign: touch.campaign, channelId: null }
              : null,
            isMember: noise('member') < 0.25,
            isVip: orderCount >= 8 && noise('vip') < 0.5,
            consent: {
              marketingState: consentState(noise('consent-email')),
              analytics: consentState(noise('consent-analytics')),
              advertising: consentState(noise('consent-advertising')),
              email: consentState(noise('consent-email')),
              push: 'unknown',
              updatedAt: `${registeredDay}T10:06:00Z`,
            },
          }
        : null,
  }
}

/** A customer's row for a range: lifetime figures from every order, the range's from its own. */
function rowFor(customer: DemoCustomer, range: DateWindow): CustomerRow {
  const { orders } = customer
  const inRange = orders.filter((order) => order.day >= range.from && order.day <= range.to)
  const sum = (list: readonly DemoOrder[], read: (order: DemoOrder) => number) => list.reduce((total, order) => total + read(order), 0)
  const lifetimeRevenueMinor = sum(orders, (order) => order.revenueMinor)
  const first = orders[0]
  const { record } = customer
  return {
    customerId: customerIdOf(customer),
    customerRef: maskCustomerRef(customer.externalId),
    websiteId: customer.websiteId,
    environment: 'production',
    synced: record !== null,
    registeredAt: customer.registeredDay ? `${customer.registeredDay}T10:05:00Z` : null,
    firstTouch: record?.firstTouch ?? null,
    isMember: record?.isMember ?? null,
    isVip: record?.isVip ?? null,
    consent: record?.consent ?? null,
    segments: null,
    firstPurchaseAt: first?.at ?? null,
    lastPurchaseAt: orders[orders.length - 1]?.at ?? null,
    orders: orders.length,
    tickets: sum(orders, (order) => order.tickets),
    lifetimeRevenueMinor,
    refundsMinor: sum(orders, (order) => order.refundMinor),
    aovMinor: orders.length > 0 ? Math.round(lifetimeRevenueMinor / orders.length) : null,
    rangeOrders: inRange.length,
    rangeTickets: sum(inRange, (order) => order.tickets),
    rangeRevenueMinor: sum(inRange, (order) => order.revenueMinor),
    rangeRefundsMinor: sum(inRange, (order) => order.refundMinor),
    customerType: inRange.length === 0 ? null : first && first.day >= range.from ? 'new' : 'returning',
  }
}

/** A listed customer: who, their row for the period, and the demo's own id (never on the row: the row is masked). */
interface Listed {
  customer: DemoCustomer
  row: CustomerRow
  externalId: string
}

/** README `basis`: the period's buyers, its registrations, or everyone a record or an order names. */
function basisLists(basis: CustomerBasis, { customer, row }: Listed, range: DateWindow): boolean {
  if (basis === 'purchased') return row.rangeOrders > 0
  if (basis === 'registered') return customer.registeredDay !== null && customer.registeredDay >= range.from && customer.registeredDay <= range.to
  return row.synced || row.orders > 0
}

/** The customer type and the record filters; a record filter leaves out an unsynced customer. */
function filtersKeep(query: CustomersQuery, row: CustomerRow): boolean {
  const consent = query.consent ?? []
  const readsRecord = query.member !== undefined || query.vip !== undefined || consent.length > 0
  if (query.customerType && row.customerType !== query.customerType) return false
  if (readsRecord && !row.synced) return false
  if (query.member !== undefined && row.isMember !== query.member) return false
  if (query.vip !== undefined && row.isVip !== query.vip) return false
  return consent.length === 0 || (row.consent !== null && consent.includes(row.consent.marketingState))
}

function compareTie(a: Listed, b: Listed): number {
  return (
    a.row.websiteId.localeCompare(b.row.websiteId) ||
    a.row.environment.localeCompare(b.row.environment) ||
    a.externalId.localeCompare(b.externalId)
  )
}

function periodFor(window: DateWindow, label: string): PeriodInfo {
  return { from: window.from, to: window.to, label, compareLabel: null, previousFrom: null, previousTo: null }
}

export class MockCustomersStore {
  constructor(
    private readonly dataset: DemoDataset,
    private readonly currency: string,
  ) {}

  private customers: DemoCustomer[] | null = null

  /** Every demo customer, built on first use. */
  private pool(): DemoCustomer[] {
    if (!this.customers) {
      const { tenant, today } = this.dataset
      const website = tenant.websites.find((candidate) => candidate.environment === 'production')?.id ?? 'web_demo'
      const unit = 10 ** minorUnitExponent(this.currency)
      this.customers = Array.from({ length: POOL_SIZE }, (_, index) => demoCustomer(tenant.seed, today, website, index, unit))
    }
    return this.customers
  }

  /** The customers the query lists, sorted, before paging; `search` narrows them to one person. */
  private matching(query: CustomersQuery, search?: CustomerSearch) {
    const resolved = resolvePeriod(query.filters, this.dataset.now)
    const range = resolved.current
    const basis = query.basis ?? DEFAULT_CUSTOMER_BASIS
    const inScope = scopeFor(this.dataset.tenant, query.filters).includesProduction
    const website = query.filters.dimensions.website
    const listed = this.pool()
      .filter((customer) => inScope && (!website || website === customer.websiteId))
      .map((customer): Listed => ({ customer, row: rowFor(customer, range), externalId: customer.externalId }))
      .filter((entry) => basisLists(basis, entry, range) && filtersKeep(query, entry.row) && (!search || searchFinds(search, entry)))
    const sort = query.sort ?? DEFAULT_CUSTOMER_SORT
    const order = query.order ?? DEFAULT_SORT_ORDER
    const rows = sortCustomers(listed, ({ row }) => row, sort, order, compareTie).map(({ row }) => row)
    return { rows, basis, sort, order, period: periodFor(range, resolved.label) }
  }

  list(query: CustomersQuery, search?: CustomerSearch): { data: CustomersList; period: PeriodInfo } {
    const { rows, basis, sort, order, period } = this.matching(query, search)
    const limit = query.limit ?? DEFAULT_LIMIT
    const offset = query.offset ?? 0
    const page = rows.slice(offset, offset + limit)
    const notAvailable: CustomersList['notAvailable'] = { segments: 'no_segments_yet' }
    if (page.some((row) => !row.synced)) for (const field of RECORD_FIELDS) notAvailable[field] = 'customer_not_synced'
    return {
      period,
      data: {
        rows: page,
        total: rows.length,
        basis,
        sort,
        order,
        limit,
        offset,
        notAvailable,
        currency: this.currency,
        timezone: DEMO_TIMEZONE,
      },
    }
  }

  /** The README's CSV: every matching row (up to 5,000), masked, money in major units, with the route's headers. */
  csv(query: CustomersQuery): CsvFile {
    const { rows, period } = this.matching(query)
    return {
      blob: csvBlob(csvColumns(this.currency), rows.slice(0, CUSTOMERS_CSV_MAX_ROWS)),
      filename: customersCsvFilename(period.from, period.to),
      truncated: rows.length > CUSTOMERS_CSV_MAX_ROWS,
      totalRows: rows.length,
    }
  }

  /** The reveal: the demo's id, marked as the demo's so it is never mistaken for a real customer's. A 404 for a customer the demo does not hold. */
  reveal(customerId: string): CustomerReveal {
    const customer = this.pool().find((candidate) => customerIdOf(candidate) === customerId)
    if (!customer) throw new ApiError(404, 'tenant_scope_error', 'Customer not found.')
    return { customerId, websiteId: customer.websiteId, environment: 'production', externalCustomerId: `DEMO-${customer.externalId}` }
  }
}

/**
 * The demo's search: an id exactly (the demo's `DEMO-` mark allowed), whether or not
 * the record has synced; or `customer<id>@example.com`, which reads the synced record:
 * only a customer whose record has synced and who granted advertising consent.
 */
function searchFinds(search: CustomerSearch, { row, externalId }: Listed): boolean {
  if ('externalCustomerId' in search) {
    return search.externalCustomerId.trim().replace(/^demo-/i, '') === externalId
  }
  const address = search.email.trim().toLowerCase()
  return row.synced && row.consent?.advertising === 'granted' && address === `customer${externalId}@example.com`
}

/** The README's CSV columns, in order, each with its cell. */
function csvColumns(currency: string): readonly MockCsvColumn<CustomerRow>[] {
  return [
    ['customer_id', (row) => csvText(row.customerId)],
    ['customer_ref', (row) => csvText(row.customerRef)],
    ['website_id', (row) => csvText(row.websiteId)],
    ['environment', (row) => csvText(row.environment)],
    ['synced', (row) => csvPlain(row.synced)],
    ['registered_at', (row) => csvInstant(row.registeredAt)],
    ['first_purchase_at', (row) => csvInstant(row.firstPurchaseAt)],
    ['last_purchase_at', (row) => csvInstant(row.lastPurchaseAt)],
    ['orders', (row) => csvPlain(row.orders)],
    ['tickets', (row) => csvPlain(row.tickets)],
    ['currency', () => csvText(currency)],
    ['lifetime_revenue', (row) => csvMoney(row.lifetimeRevenueMinor, currency)],
    ['refunds', (row) => csvMoney(row.refundsMinor, currency)],
    ['aov', (row) => csvMoney(row.aovMinor, currency)],
    ['range_orders', (row) => csvPlain(row.rangeOrders)],
    ['range_tickets', (row) => csvPlain(row.rangeTickets)],
    ['range_revenue', (row) => csvMoney(row.rangeRevenueMinor, currency)],
    ['range_refunds', (row) => csvMoney(row.rangeRefundsMinor, currency)],
    ['customer_type', (row) => csvText(row.customerType)],
    ['first_touch_at', (row) => csvInstant(row.firstTouch?.touchedAt ?? null)],
    ['first_touch_source', (row) => csvText(row.firstTouch?.source ?? null)],
    ['first_touch_medium', (row) => csvText(row.firstTouch?.medium ?? null)],
    ['first_touch_campaign', (row) => csvText(row.firstTouch?.campaign ?? null)],
    ['first_touch_channel_id', (row) => csvText(row.firstTouch?.channelId ?? null)],
    ['is_member', (row) => csvPlain(row.isMember)],
    ['is_vip', (row) => csvPlain(row.isVip)],
    ['marketing_consent', (row) => csvText(row.consent?.marketingState ?? null)],
    ['consent_analytics', (row) => csvText(row.consent?.analytics ?? null)],
    ['consent_advertising', (row) => csvText(row.consent?.advertising ?? null)],
    ['consent_email', (row) => csvText(row.consent?.email ?? null)],
    ['consent_push', (row) => csvText(row.consent?.push ?? null)],
    ['consent_updated_at', (row) => csvInstant(row.consent?.updatedAt ?? null)],
  ]
}
